/**
 * The four parts, one section each — Reach, Connect, Sense, Flow — each with
 * a few words and a picture of it working.
 *
 * WHY ONE SECTION PER PART. The owner's brief: "it is too much on email,
 * whatsapp, meta ads. i am not selling that, i am selling reach, connect, sense
 * and flow." So the parts are the sections' subjects, and channels appear only
 * as small supporting details (two chips under Connect, icons inside the
 * pictures). Each section is a kicker, a headline of a few words, at most two
 * short lines, and an animated illustration (components/illustrations.tsx).
 *
 * WHAT EACH MAY CLAIM — the page's standing rules, kept:
 *   - Reach needs the reader's own Meta ad account, and says so in a plain
 *     one-line note under its copy (no status tag: the owner asked for the
 *     "Available" tags to go, Oct 2026);
 *     ads are published paused and a person starts them.
 *   - Connect sends through the reader's own accounts.
 *   - Sense drafts; a person decides. No part acts on its own.
 *   - Flow is launched by a person; every message in it gets quiet hours and
 *     the frequency cap.
 *   - Nothing is said about sending converted people to Meta: not built.
 *
 * LAYOUT. Two columns from `lg`, the picture alternating sides; on a phone the
 * picture comes first, so each section opens on the thing in action.
 */
import type { ReactNode } from 'react';

import { cn } from '@wend/ui';

import { ConnectIllustration, FlowIllustration, ReachIllustration, SenseIllustration } from '../components/illustrations';

export interface PillarCopy {
  readonly id: 'reach' | 'connect' | 'sense' | 'flow';
  readonly name: string;
  /** A plain requirement line under the copy, if the part has one. */
  readonly note?: string;
  readonly headline: string;
  readonly line: string;
  readonly chips: readonly string[];
  readonly picture: ReactNode;
}

export const PILLARS: readonly PillarCopy[] = [
  {
    id: 'reach',
    name: 'Reach',
    headline: 'Ads that turn into people.',
    line: 'Publish ads to your own Meta ad account, paused until you start them. Every tap lands as a person.',
    chips: ['Click-to-WhatsApp leads', 'Spend cap you set'],
    picture: <ReachIllustration />,
  },
  {
    id: 'connect',
    name: 'Connect',
    headline: 'Talk to the people you have.',
    line: 'Pick a segment and send from your own accounts. Watch each message go from sent to replied.',
    chips: ['Email', 'WhatsApp'],
    picture: <ConnectIllustration />,
  },
  {
    id: 'sense',
    name: 'Sense',
    headline: 'See what changed. Get a draft.',
    line: 'Sense reads what was delivered, replied to and converted, and points at what is worth doing.',
    chips: ['Drafts only · you launch'],
    picture: <SenseIllustration />,
  },
  {
    id: 'flow',
    name: 'Flow',
    headline: 'Journeys that react to each person.',
    line: 'Message, wait, branch on a reply, stop at the goal. Built by you or drafted by Sense, always launched by you.',
    chips: ['Quiet hours', 'Frequency cap', 'Holdout'],
    picture: <FlowIllustration />,
  },
];

function Pillar({ pillar, index }: { pillar: PillarCopy; index: number }) {
  const flip = index % 2 === 1;
  return (
    <section id={pillar.id} aria-labelledby={`${pillar.id}-title`} className="scroll-mt-20 px-5 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className={cn('reveal', flip && 'lg:order-last')}>{pillar.picture}</div>
        <div className="reveal">
          <p className="font-mono text-[0.75rem] uppercase tracking-[0.18em] text-brand-ink">
            0{index + 1} · {pillar.name}
          </p>
          <h2 id={`${pillar.id}-title`} className="mt-5 text-balance text-3xl font-semibold tracking-tight text-text sm:text-[2.75rem] sm:leading-[1.1]">
            {pillar.headline}
          </h2>
          <p className="mt-5 max-w-md text-pretty text-lg leading-relaxed text-muted">{pillar.line}</p>
          {pillar.note !== undefined && (
            <p data-requirement="" className="mt-3 text-[0.9rem] text-text">
              {pillar.note}
            </p>
          )}
          <ul className="mt-6 flex flex-wrap gap-2" aria-label={`${pillar.name} details`}>
            {pillar.chips.map((chip) => (
              <li key={chip} className="rounded-full border border-line-strong bg-white/[0.04] px-3 py-1 text-[0.82rem] text-muted">
                {chip}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function Pillars() {
  return (
    <div id="parts" className="border-b border-line">
      {PILLARS.map((pillar, index) => (
        <Pillar key={pillar.id} pillar={pillar} index={index} />
      ))}
    </div>
  );
}
